import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useEntityConfig } from '@/hooks/useEntityConfig.tsx';
import { crmOriginOf, isEmbedPath, readCrmMessage } from '@/utils/b2coreEmbed.ts';

const EMBED_PALETTE_ATTRIBUTE = 'data-b2core-embed';
const EMBED_LOADING_ATTRIBUTE = 'data-b2core-embed-loading';
const CRM_THEME_WAIT_MS = 15_000;

const B2CoreEmbedPalette = (): null => {
  const config = useEntityConfig();
  const { pathname } = useLocation();
  const isEmbedded = window.parent !== window && isEmbedPath(pathname);
  const crmOrigin = crmOriginOf(config.VITE_CLIENT_PORTAL_URL);
  const [isCrmThemeKnown, setIsCrmThemeKnown] = useState(false);

  useEffect(() => {
    if (!isEmbedded || isCrmThemeKnown) {
      return;
    }

    const handleMessage = (event: MessageEvent): void => {
      if (readCrmMessage(event, crmOrigin)?.type === 'embed-theme-change') {
        setIsCrmThemeKnown(true);
      }
    };

    const fallbackTimer = window.setTimeout(() => setIsCrmThemeKnown(true), CRM_THEME_WAIT_MS);

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
      window.clearTimeout(fallbackTimer);
    };
  }, [isEmbedded, isCrmThemeKnown, crmOrigin]);

  useLayoutEffect(() => {
    const root = document.documentElement;

    if (isEmbedded) {
      root.setAttribute(EMBED_PALETTE_ATTRIBUTE, config.VITE_ENTITY);
    } else {
      root.removeAttribute(EMBED_PALETTE_ATTRIBUTE);
    }

    root.toggleAttribute(EMBED_LOADING_ATTRIBUTE, isEmbedded && !isCrmThemeKnown);
  }, [isEmbedded, isCrmThemeKnown, config.VITE_ENTITY]);

  return null;
};

export default B2CoreEmbedPalette;
